import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Grid2 as Grid, IconButton, Link, Stack, Toolbar, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useDragDropManager } from 'react-dnd';
import Sticky from 'react-sticky-el/lib/basic-version';
import { useAppStore } from '../../app/store';
import { useCalendar } from '../calendar.util';
import MonthlyDay from './monthly.day';
import pluralize from 'pluralize';

export default function MonthlyView(props) {
	const { user } = useAppStore();
	const unit = 'month';
	const theme = useTheme();

	const [toggleRender, setToggleRender] = useState(false);

	const dragDropManager = useDragDropManager();
	dragDropManager.monitor.subscribeToStateChange(() => {
		if (dragDropManager.monitor.didDrop()) {
			setToggleRender(!toggleRender);
		}
	});

	const getFirstDayOfMonth = (referenceDate) => {
		return moment(referenceDate).startOf(unit);
	}

	const getLastDayOfMonth = (referenceDate) => {
		return moment(referenceDate).endOf(unit);
	}

	const calendar = useCalendar(unit, getFirstDayOfMonth, getLastDayOfMonth);
	const numDaysToDisplay = ((user.prefs.monthlyIncludeWeekends == true) ? 7 : 5);
	let calendarHeader = [];
	let weeks = [];
	let currentWeek = [];
	let numHolidays = 0;

	for (let i = 0; i < 7; i++) {
		const date = moment().day(i + (+user.prefs.monthlyStartsOn || 0));
		const label = date.format('dddd');
		const className = 'header-week' + (calendarHeader.length == 0 ? ' first-of-week' : '');

		let isWeekend = date.day() == 0 || date.day() == 6;
		if (!isWeekend || (user.prefs.monthlyIncludeWeekends == true)) {
			calendarHeader.push(
				<Grid className={className} size={1} sx={{ p: 1, height: 40, backgroundColor: theme.palette.background.weekend }} key={'head-' + calendarHeader.length}>
					<Typography variant="h5">{label}</Typography>
				</Grid>
			);
		}
	}

	let weekendBgColour = theme.palette.background.weekend;

	let date = moment(calendar.startDate);
	do {
		let className = weeks.length == 0 ? 'first-week' : '';
		className += currentWeek.length == 0 ? ' first-of-week' : '';

		let dayOfWeek = moment(date).day();
		let isWeekend = (dayOfWeek === 0) || (dayOfWeek === 6);
		if (isWeekend && !(user.prefs.monthlyIncludeWeekends == true)) {
			date = moment(date).add(1, 'day');
			continue;
		}

		let dayOfWeekOffset = dayOfWeek - ((user.prefs.monthlyIncludeWeekends == true) ? (+user.prefs.monthlyStartsOn || 0) : 1);
		dayOfWeekOffset = dayOfWeekOffset < 0 ? dayOfWeekOffset + 7 : dayOfWeekOffset;

		if (dayOfWeekOffset != (currentWeek.length % numDaysToDisplay) && currentWeek.length <= numDaysToDisplay) {
			currentWeek.push(<Grid className={className} size={1} sx={{ p: 1, backgroundColor: weekendBgColour, minHeight: 100 }} key={'empty-before-' + currentWeek.length} />);
			continue;
		}

		let isToday = date.isSame(new Date(), 'day');
		let isHoliday = calendar.isHoliday(date);
		numHolidays += isHoliday && !isWeekend ? 1 : 0;

		const bgColours = theme.palette.background;
		let bgColour = isToday ? bgColours.today : isWeekend ? bgColours.weekend : isHoliday ? bgColours.holiday : '';

		const dayTodos = _.filter(calendar.todos, { dueDate: date.valueOf() });
		currentWeek.push(
			<Grid className={className} size={1} sx={{ p: 1, backgroundColor: bgColour, display: (!isWeekend || (user.prefs.monthlyIncludeWeekends == true)) ? 'initial' : 'none', minHeight: 100 }} key={date}>
				<Stack direction="column" sx={{ mb: 1 }}>
					<Link href={"/calendar/daily?date=" + date} color="inherit">
						<Typography variant="h5" sx={{ textAlign: 'right' }}>{date.format('D')}</Typography>
					</Link>
				</Stack>

				{isHoliday ?
					<Typography variant="h5" color="textDisabled" sx={{ pt: 3, pb: 6, textAlign: 'center' }}>{calendar.getHoliday(date).name}</Typography>
					:
					<MonthlyDay date={date} todos={dayTodos} activities={calendar.filteredActivities} format="D" textAlign="right" groupBy={user.prefs.focus} isHoliday={isHoliday} />
				}
			</Grid>
		);

		if (currentWeek.length == numDaysToDisplay) {
			weeks = _.concat(weeks, currentWeek);
			currentWeek = [];
		}

		date = moment(date).add(1, 'day');
	} while (date.isBefore(calendar.endDate));

	if (currentWeek.length > 0) {
		while (currentWeek.length < numDaysToDisplay) {
			let isWeekend = (currentWeek.length === 6);
			if (!isWeekend || (user.prefs.monthlyIncludeWeekends == true)) {
				currentWeek.push(<Grid size={1} sx={{ p: 1, backgroundColor: weekendBgColour, minHeight: 100 }} key={'empty-after-' + currentWeek.length} />);
			}
		}
		weeks = _.concat(weeks, currentWeek);
	}

	const previousDate = moment(calendar.startDate).subtract(1, unit);
	const previousLabel = previousDate.format('MMMM');
	const nextDate = moment(calendar.startDate).add(1, unit);
	const nextLabel = nextDate.format('MMMM');
	const label = calendar.startDate.format('MMMM YYYY');
	const includesToday = moment().isAfter(calendar.startDate) && moment().isBefore(calendar.endDate);

	const gridBorder = {
		'--border': '2px solid #888',
		'--border2': '1px solid #888',

		'& > div': {
			borderBottom: 'var(--border)',
			borderRight: 'var(--border)',
		},
		'& > div.first-of-week': {
			borderLeft: 'var(--border)',
		},
		'& > div.first-week': {
			borderTop: 'none',
		},
		'& > div.header-week': {
			borderTop: 'var(--border)',
		},
		'& > div.header-week:first-of-type': {
			borderTopLeftRadius: 4,
		},
		'& > div.header-week:last-child': {
			borderTopRightRadius: 4,
			borderBottomRightRadius: 0,
		},
		'& > div.first-of-week:last-of-type': {
			borderBottomLeftRadius: 40,
		},
		'& > div:last-child': {
			borderBottomRightRadius: 4,
		},
	};
	const dateSx = {
		'input': { display: 'none' },
		'fieldset': { border: 'none' },
		'.MuiInputAdornment-root': { margin: 0 },
	};

	return (
		<Box sx={{ p: 2 }}>
			<Sticky stickyStyle={{ backgroundColor: theme.palette.background.default, zIndex: 10, top: theme.mixins.toolbar["@media (min-width:600px)"].minHeight }} topOffset={-theme.mixins.toolbar["@media (min-width:600px)"].minHeight}>
				<Toolbar disableGutters sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
					<Box />

					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<IconButton onClick={() => calendar.moveCalendar(-1)}>
							<ChevronLeftIcon />
						</IconButton>
						<Stack spacing={0} direction="column" alignItems="center" sx={{ minWidth: 150 }}>
							<Typography variant="h5">{label}</Typography>
							{numHolidays > 0 && <Typography>{numHolidays} {pluralize('Day', numHolidays)} Break</Typography>}
						</Stack>
						<IconButton onClick={() => calendar.moveCalendar(1)}>
							<ChevronRightIcon />
						</IconButton>
					</Box>
					<Stack justifyContent="flex-end" alignItems="center" gap={0}>
						<DatePicker sx={dateSx} value={calendar.startDate || ''} views={['year', 'month']} openTo='month' onChange={(e) => calendar.setCalendar(e)} />
					</Stack>
				</Toolbar>

				<Box sx={{ mt: 1, mb: 0 }}>
					<Grid container columns={numDaysToDisplay} sx={gridBorder} spacing={0}>
						{calendarHeader}
					</Grid>
				</Box>
			</Sticky>

			<Box sx={{}}>
				<Grid container columns={numDaysToDisplay} sx={gridBorder} spacing={0}>
					{weeks}
				</Grid>
			</Box>
			{calendar.fabControl}
		</Box>
	);
}